// frontend/src/components/deals/tabs/AppointmentsTab.tsx
import { useState } from "react";
import { CalendarClock, MapPin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import EmptyState from "@/components/common/EmptyState";
import SectionCard from "@/components/common/SectionCard";
import { Skeleton } from "@/components/ui/skeleton";
import AppointmentDialog from "@/components/deals/dialogs/AppointmentDialog";
import { APPOINTMENT_KIND_LABELS } from "@/constant/deals.constant";
import { useCancelAppointment, useGetAppointments, usePatchAppointment, usePostAppointment } from "@/hooks/useDeals";
import type { Appointment, AppointmentPayload, DealDetail } from "@/types/deals.types";
import { cn } from "@/utils/common.utils";
import { formatAppointmentWhen } from "@/utils/appointments.utils";

export default function AppointmentsTab({ deal, canAct, canSchedule = canAct }: { deal: DealDetail; canAct: boolean; canSchedule?: boolean }) {
  const [showPast, setShowPast] = useState(false);
  const [editing, setEditing] = useState<Appointment | null>(null);
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<Appointment | null>(null);
  const { data: appointments = [], isLoading, isError, refetch } = useGetAppointments(deal.id, showPast);
  const { mutate: post, isPending: posting } = usePostAppointment();
  const { mutate: patch, isPending: patching } = usePatchAppointment();
  const { mutateAsync: cancel } = useCancelAppointment();

  const save = (payload: AppointmentPayload) => {
    const done = { onSuccess: () => { setCreating(false); setEditing(null); } };
    if (editing) patch({ appointmentId: editing.id, dealId: deal.id, payload }, done);
    else post({ dealId: deal.id, payload }, done);
  };

  return (
    <section data-testid="appointments-tab">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="dashboardOutline" size="sm" onClick={() => setShowPast(!showPast)} data-testid="appointments-toggle-past">
          {showPast ? "Hide past and cancelled" : "Show past and cancelled"}
        </Button>
        {canAct && (
          <Button variant="dashboardPrimary" disabled={!canSchedule} title={!canSchedule ? "Update your subscription to send new invitations" : undefined} onClick={() => setCreating(true)} data-testid="appointments-new">Schedule a showing or meeting</Button>
        )}
      </div>
      {isLoading ? (
        <div role="status" aria-label="Loading appointments" className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-24 w-full" /></div>
      ) : isError ? (
        <EmptyState icon={CalendarClock} title="Couldn’t load appointments" description="Try again to see what's scheduled." action={<Button type="button" variant="dashboardOutline" onClick={() => refetch()}>Try again</Button>} />
      ) : !appointments.length ? (
        <EmptyState icon={CalendarClock} title="Nothing scheduled" description="Schedule a showing or meeting and Stoop will email everyone a calendar invite." />
      ) : (
        <SectionCard className="overflow-hidden" contentClassName="p-0">
          <ul>
            {appointments.map((a, i) => {
              const cancelled = a.status === "cancelled";
              return (
                <li key={a.id} className={cn("flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:px-6", i > 0 && "border-t border-dash-border", cancelled && "opacity-60")} data-testid={`appointment-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-dash-muted">
                      {APPOINTMENT_KIND_LABELS[a.kind]}{cancelled ? " · Cancelled" : ""}{a.last_change_source === "google" ? " · Changed in Google Calendar" : ""}
                    </p>
                    <p className={cn("mt-0.5 break-words font-medium text-dash-ink", cancelled && "line-through")}>{a.title}</p>
                    <p className="mt-1 text-sm text-dash-muted">{formatAppointmentWhen(a)}</p>
                    {a.location && <p className="mt-1 flex items-start gap-1.5 text-xs text-dash-faint"><MapPin className="mt-0.5 size-3.5 shrink-0" /><span className="min-w-0 break-words">{a.location}</span></p>}
                    {!!a.attendees.length && <p className="mt-1 flex items-start gap-1.5 text-xs text-dash-faint"><Users className="mt-0.5 size-3.5 shrink-0" /><span className="min-w-0 break-words">{a.attendees.map((x) => x.name).join(", ")}</span></p>}
                  </div>
                  {canAct && !cancelled && (
                    <div className="flex gap-2">
                      <Button variant="dashboardOutline" size="sm" onClick={() => setEditing(a)} data-testid={`appointment-edit-${a.id}`}>Edit</Button>
                      <Button variant="ghost" size="sm" className="text-dash-rust hover:text-dash-rust" onClick={() => setCancelling(a)} data-testid={`appointment-cancel-${a.id}`}>Cancel</Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </SectionCard>
      )}
      <AppointmentDialog open={creating || Boolean(editing)} onClose={() => { setCreating(false); setEditing(null); }} deal={deal} appointment={editing} busy={posting || patching} onSubmit={save} />
      {cancelling && (
        <ConfirmDialog
          open
          onOpenChange={(next) => { if (!next) setCancelling(null); }}
          title={`Cancel this ${APPOINTMENT_KIND_LABELS[cancelling.kind].toLowerCase()}?`}
          description="This removes it from the upcoming list and sends attendees a cancellation email with a calendar file."
          confirmLabel="Cancel it"
          busyLabel="Cancelling…"
          onConfirm={async () => {
            try {
              await cancel({ appointmentId: cancelling.id, dealId: deal.id, expectedSequence: cancelling.sequence });
              setCancelling(null);
            } catch {
              // The mutation shows the error; recover the list for a fresh decision.
              setCancelling(null);
              await refetch();
            }
          }}
          testId="appointment-cancel-confirm"
        />
      )}
    </section>
  );
}
